import React, { useState } from 'react';
import axios from 'axios';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { resolveImageUrl } from './ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const B2BCTA = () => {
  const { toast } = useToast();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: '', company: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast({
        title: 'Inquiry sent',
        description: 'Our trade team will reach out within one business day.',
      });
      setForm({ name: '', company: '', email: '', message: '' });
    } catch (err) {
      toast({
        title: 'Could not send inquiry',
        description: err.response?.data?.detail || 'Please try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden relative bg-gradient-to-br from-slate-900 via-[#0F172A] to-[#1E293B] text-white p-8 sm:p-12 lg:p-16 grain">
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#0078D4]/30 blur-3xl" />
        <div className="absolute -left-20 bottom-0 w-80 h-80 rounded-full bg-purple-500/15 blur-3xl" />

        {settings?.b2b_cta_image && (
          <img
            src={resolveImageUrl(settings.b2b_cta_image)}
            alt=""
            className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-30 mix-blend-luminosity pointer-events-none"
          />
        )}

        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] font-bold tracking-[0.2em] text-cyan-300 uppercase mb-3">
              {settings?.b2b_eyebrow || 'Open a trade account'}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {settings?.b2b_heading || 'Custom pricing for retailers, hotels & offices.'}
            </h2>
            <p className="mt-4 text-blue-100/90 max-w-md text-sm sm:text-base">
              {settings?.b2b_desc ||
                "Tell us your monthly volume and we'll send tailored quotes, delivery schedules and a dedicated account manager."}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-blue-100/80">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-300" /> No minimum lock-in</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-300" /> Credit terms for verified accounts</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-300" /> Multi-location billing supported</li>
            </ul>
          </div>

          <form onSubmit={submit} className="bg-white text-slate-900 rounded-2xl p-6 sm:p-7 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-lg" data-testid="inquiry-name" />
              <Input required placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="h-11 rounded-lg" data-testid="inquiry-company" />
            </div>
            <Input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-lg" data-testid="inquiry-email" />
            <Textarea rows={3} placeholder="What products and how often? (e.g., 200 cartons of Saroj 500ml monthly)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-lg" data-testid="inquiry-message" />
            <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:opacity-70 text-white font-semibold btn-lift" data-testid="inquiry-submit">
              {submitting ? 'Sending…' : 'Request a quote'} <Send className="w-4 h-4 ml-1.5" />
            </Button>
            <p className="text-[11px] text-slate-500 text-center">
              We respond within 1 business day. No obligation.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default B2BCTA;
